import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  viewChild,
  signal,
  Signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatToolbarModule } from '@angular/material/toolbar';

import { map, groupBy, orderBy, filter } from 'lodash-es';
import { Project, Participant, Assessment } from '../models';
import { ApiService } from '../api.service';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { ParticipantDomainService } from '../domain-services/participant-domain.service';
import { ACTIVE_CLIENT } from '../../main';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-participant-list',
  standalone: true,
  templateUrl: './participant-list.component.html',
  styleUrls: ['./participant-list.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatToolbarModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantListComponent {
  paginator = viewChild(MatPaginator);
  sort = viewChild(MatSort);

  project = input.required<Project>();
  participants = input.required<Participant[]>();
  assessments = input.required<Assessment[]>();

  private readonly projectDomain = inject(ProjectDomainService);
  private readonly participantDomain = inject(ParticipantDomainService);

  projectView = computed<Project>(() => this.projectDomain.currentProject() ?? this.project());

  // Prefer domain-backed participants so inline edits propagate
  // back into the table without requiring a full resolver reload.
  participantsForDisplay = computed<Participant[]>(() => {
    return this.participantDomain.projectParticipants() ?? this.participants();
  });

  viewData = computed(() => {
    const groupedAssessments = groupBy(this.assessments(), 'participantId');
    const rows = map(this.participantsForDisplay(), (participant) => {
      const x = groupedAssessments[participant.id];

      return {
        ...participant,
        total: x.length,
        completed: filter(x, (a) => a.completedDate).length,
        latest: orderBy(x, ['completedDate'], ['desc'])[0].completedDate,
        assessments: x,
      };
    });

    return new MatTableDataSource<ViewData>(rows);
  });

  // We can safely say there are no ready reports
  // when no participant has any completed assessments.
  hasAnyPotentialReports = computed(() => {
    const data = this.viewData().data as ViewData[];
    return data.some((row) => row.completed > 0);
  });

  constructor() {
    effect(() => {
      const p = this.paginator();
      if (p) this.viewData().paginator = p;
      const s = this.sort();
      if (s) {
        this.viewData().sort = s;
        this.viewData().sortingDataAccessor = (item: any, property: string) => {
          if (property === 'completionDates') {
            if (item.latest) {
              return Date.parse(item.latest);
            }
            return item.completed;
          }
          const value = item[property];
          return typeof value === 'string' ? value.toLowerCase() : value;
        };
        this.viewData().sort?.sort({
          id: 'completionDates',
          start: 'asc',
          disableClear: false,
        });
      }
    });
  }

  selected: WritableSignal<{ participant: Participant; assessments: Assessment[] | null } | null> =
    signal(null);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  selectParticipant(row: ViewData) {
    this.selected.set({ participant: row as Participant, assessments: row.assessments });
    this.router.navigate([row.id], {
      relativeTo: this.route,
      state: { showLoading: false },
    });
  }

  /* Download Project Reports */
  private apiService = inject(ApiService);
  private activeClient = inject(ACTIVE_CLIENT);
  error = signal<string | null>(null);
  downloading = signal(false);
  download() {
    this.error.set(null);
    this.downloading.set(true);

    this.apiService
      .downloadReports({ clientId: this.activeClient, projectId: this.projectView().id })
      .pipe(finalize(() => this.downloading.set(false)))
      .subscribe({
        next: ({ url, revoke }) => {
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.projectView().id}_reports.zip`;
          a.click();
          // revoke after the browser processes the click
          requestAnimationFrame(revoke);
        },
        error: (err) => this.error.set('Failed to download.'),
      });
  }
}

export interface ViewData extends Participant {
  total: number;
  completed: number;
  latest: string | null;
  assessments: Assessment[] | null;
}
